import { useEffect, useState } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { DatoQueSeQueda, FichaCliente, LineaDeHistorial, ResumenDeCliente, Sugerencia } from '../../../api/ops/clientesDuplicados';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Casilla } from '../../../componentes/Casilla';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando, EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { claveIdempotencia } from '../../../api/comun';
import { estadoDeError } from '../../../logica/estados';
import { cuando, diaMes, importe } from '../../../logica/fechasCortas';
import { Ayuda } from '../../../componentes/Ayuda';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, CANALES, DATOS_QUE_SE_QUEDAN, ESTADO_PERMISO, FINALIDADES, IDIOMA, MOTIVO, ORIGEN, TEXTOS, primerNombre } from './textos';

/** Qué abre el panel: una sugerencia de la lista o «Fusionar dos clientes» a mano. */
export type Apertura = { tipo: 'sugerencia'; sugerencia: Sugerencia } | { tipo: 'manual' };

type Pestana = 'elegir' | 'comparar' | 'historial' | 'fusion';
type Lado = 0 | 1;

interface Props {
    ops: Operaciones;
    negocio: string;
    movil: boolean;
    apertura: Apertura | null;
    puedeFusionar: boolean;
    soloLectura: boolean;
    onCerrar: () => void;
    onDescartar: (s: Sugerencia) => void;
    onFusionada: (texto: string) => void;
}

interface Datos {
    fichas: [FichaCliente, FichaCliente];
    conNotas: [boolean, boolean];
    conPuerta: [boolean, boolean];
    etiquetas: Readonly<Record<string, string>>;
    campos: Readonly<Record<string, string>>;
}

const PESTANAS: Record<Pestana, [string, 'search' | 'git-compare' | 'history' | 'git-merge']> = {
    elegir: ['Elegir', 'search'],
    comparar: ['Comparar', 'git-compare'],
    historial: ['Historial', 'history'],
    fusion: ['Fusión', 'git-merge'],
};

function inicialesDe(nombre: string): string {
    return nombre
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((p) => p.charAt(0).toLocaleUpperCase('es'))
        .join('');
}

function valorDeCampo(v: unknown): string {
    if (Array.isArray(v)) {
        return v.map(String).join(', ');
    }
    if (typeof v === 'boolean') {
        return v ? 'Sí' : 'No';
    }
    return typeof v === 'string' || typeof v === 'number' ? String(v) : '';
}

/** El valor de un dato local de la ficha, como se enseña («Vacío» si no lo tiene). */
function valorDe(f: FichaCliente, dato: DatoQueSeQueda, campos: Readonly<Record<string, string>>): string {
    switch (dato) {
        case 'local_display_name':
            return f.local.display_name ?? '';
        case 'local_phone':
            return f.local.phone ?? '';
        case 'local_email':
            return f.local.email ?? '';
        case 'locale':
            return f.locale === undefined ? '' : (IDIOMA[f.locale] ?? f.locale);
        case 'field_values':
            return Object.entries(f.field_values ?? {})
                .map(([clave, v]) => [campos[clave] ?? clave, valorDeCampo(v)] as const)
                .filter(([, v]) => v !== '')
                .map(([k, v]) => `${k}: ${v}`)
                .join(', ');
    }
}

/** Principal por defecto: la vinculada a su Cuenta Reservaria o, si no, la que tiene más actividad, y si empatan, la más antigua. */
function principalPorDefecto(a: FichaCliente, b: FichaCliente): Lado {
    if (a.status === 'linked' && b.status !== 'linked') {
        return 0;
    }
    if (b.status === 'linked' && a.status !== 'linked') {
        return 1;
    }
    const va = a.stats?.visits_count ?? 0;
    const vb = b.stats?.visits_count ?? 0;
    if (va !== vb) {
        return va > vb ? 0 : 1;
    }
    return a.created_at <= b.created_at ? 0 : 1;
}

function quedanPorDefecto(fichas: [FichaCliente, FichaCliente], principal: Lado, campos: Readonly<Record<string, string>>): Record<DatoQueSeQueda, Lado> {
    const otra: Lado = principal === 0 ? 1 : 0;
    const quedan = {} as Record<DatoQueSeQueda, Lado>;
    for (const [dato] of DATOS_QUE_SE_QUEDAN) {
        quedan[dato] = valorDe(fichas[principal], dato, campos) === '' && valorDe(fichas[otra], dato, campos) !== '' ? otra : principal;
    }
    return quedan;
}

function telefonoDe(f: FichaCliente): string {
    return f.account?.phone ?? f.local.phone ?? '';
}
function emailDe(f: FichaCliente): string {
    return f.account?.email ?? f.local.email ?? '';
}

function estadoPermiso(f: FichaCliente, canal: string, finalidad: string): string {
    return f.consents?.find((c) => c.channel === canal && c.purpose === finalidad)?.state ?? 'none';
}

/**
 * Panel de 2.5 (RV Panel de ficha): elegir las dos fichas a mano, compararlas (principal, datos que se quedan, lo que se
 * suma y los permisos con «gana la baja», G26), el historial lado a lado y una fusión ya hecha. «Fusionar» abre la
 * ventana de confirmación; si la API responde `409 confirmation_required` (no comparten teléfono ni email, G200), la
 * ventana pide además «Confirmo que son la misma persona» y repite con `confirm_different_person_risk`.
 */
export function Comparar({ ops, negocio, movil, apertura, puedeFusionar, soloLectura, onCerrar, onDescartar, onFusionada }: Props) {
    const avisar = useAvisoBreve();
    const [pestana, setPestana] = useState<Pestana>('comparar');
    const [pareja, setPareja] = useState<[string, string] | null>(null);
    const [datos, setDatos] = useState<Datos | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [principal, setPrincipal] = useState<Lado>(0);
    const [quedan, setQuedan] = useState<Record<DatoQueSeQueda, Lado> | null>(null);
    const [historial, setHistorial] = useState<[LineaDeHistorial[], LineaDeHistorial[]] | null>(null);
    const [elegidos, setElegidos] = useState<[ResumenDeCliente | null, ResumenDeCliente | null]>([null, null]);
    const [busquedas, setBusquedas] = useState<[string, string]>(['', '']);
    const [resultados, setResultados] = useState<[ResumenDeCliente[] | null, ResumenDeCliente[] | null]>([null, null]);
    const [ventana, setVentana] = useState(false);
    const [ocupado, setOcupado] = useState(false);
    const [pideConfirmar, setPideConfirmar] = useState<string | null>(null);
    const [confirmo, setConfirmo] = useState(false);

    const sugerencia = apertura?.tipo === 'sugerencia' ? apertura.sugerencia : null;
    const manual = apertura?.tipo === 'manual';

    // Cada vez que se abre el panel, empieza de cero (se ajusta al pintar, sin un efecto que encadene otro pintado).
    const [vista, setVista] = useState<Apertura | null>(null);
    if (apertura !== vista) {
        setVista(apertura);
        setDatos(null);
        setError(null);
        setHistorial(null);
        setQuedan(null);
        setVentana(false);
        setPideConfirmar(null);
        setConfirmo(false);
        setElegidos([null, null]);
        setBusquedas(['', '']);
        setResultados([null, null]);
        if (apertura?.tipo === 'manual') {
            setPareja(null);
            setPestana('elegir');
        } else if (apertura !== null) {
            const [a, b] = apertura.sugerencia.customer_ids;
            setPareja(a === undefined || b === undefined ? null : [a, b]);
            setPestana(apertura.sugerencia.status === 'merged' ? 'fusion' : 'comparar');
        }
    }

    // Las dos fichas, con lo que se suma (notas y evaluaciones de puerta, sin contarlas: G140) y los nombres del catálogo.
    useEffect(() => {
        if (pareja === null || pestana === 'fusion') {
            return;
        }
        let vivo = true;
        const [a, b] = pareja;
        const hay = (p: Promise<{ data: unknown[] }>) => p.then((l) => l.data.length > 0).catch(() => false);
        Promise.all([
            ops.getCustomer(negocio, a),
            ops.getCustomer(negocio, b),
            hay(ops.listCustomerNotes(negocio, a, { limit: 1 })),
            hay(ops.listCustomerNotes(negocio, b, { limit: 1 })),
            hay(ops.listDoorAssessments(negocio, a, { limit: 1 })),
            hay(ops.listDoorAssessments(negocio, b, { limit: 1 })),
            ops.listTags(negocio, { include_archived: true }).catch(() => ({ data: [] })),
            ops.listCustomFields(negocio, { include_archived: true }).catch(() => ({ data: [] })),
        ])
            .then(([fa, fb, na, nb, pa, pb, tags, campos]) => {
                if (!vivo) {
                    return;
                }
                const fichas: [FichaCliente, FichaCliente] = [fa.datos, fb.datos];
                const nombresCampos = Object.fromEntries(campos.data.map((c) => [c.key, c.label]));
                const p = principalPorDefecto(fa.datos, fb.datos);
                setDatos({ fichas, conNotas: [na, nb], conPuerta: [pa, pb], etiquetas: Object.fromEntries(tags.data.map((t) => [t.id, t.name])), campos: nombresCampos });
                setPrincipal(p);
                setQuedan(quedanPorDefecto(fichas, p, nombresCampos));
                setError(null);
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pareja, pestana, intento]);

    // Historial lado a lado (las últimas líneas de cada una).
    useEffect(() => {
        if (pareja === null || pestana !== 'historial' || historial !== null) {
            return;
        }
        let vivo = true;
        Promise.all([ops.listCustomerTimeline(negocio, pareja[0], { limit: 10 }), ops.listCustomerTimeline(negocio, pareja[1], { limit: 10 })])
            .then(([a, b]) => {
                if (vivo) {
                    setHistorial([a.data, b.data]);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, pareja, pestana, historial]);

    // Buscar las fichas a mano (desde 2 letras).
    useEffect(() => {
        if (!manual) {
            return;
        }
        let vivo = true;
        const reloj = setTimeout(() => {
            ([0, 1] as const).forEach((lado) => {
                const q = busquedas[lado].trim();
                if (q.length < 2 || elegidos[lado] !== null) {
                    setResultados((r) => (lado === 0 ? [null, r[1]] : [r[0], null]));
                    return;
                }
                ops.listCustomers(negocio, { q, limit: 6 })
                    .then((l) => {
                        if (vivo) {
                            const otro = elegidos[lado === 0 ? 1 : 0]?.id;
                            const lista = l.data.filter((c) => c.status !== 'merged' && c.status !== 'anonymized' && c.id !== otro);
                            setResultados((r) => (lado === 0 ? [lista, r[1]] : [r[0], lista]));
                        }
                    })
                    .catch(() => undefined);
            });
        }, 250);
        return () => {
            vivo = false;
            clearTimeout(reloj);
        };
    }, [ops, negocio, manual, busquedas, elegidos]);

    const abierto = apertura !== null;
    const estadoSug = sugerencia?.status ?? 'open';
    const puedeActuar = puedeFusionar && pareja !== null && estadoSug === 'open' && pestana !== 'elegir' && datos !== null;
    const bloqueada = !puedeActuar;
    const fichas = datos?.fichas ?? null;
    const otra: Lado = principal === 0 ? 1 : 0;
    const P = fichas?.[principal] ?? null;
    const O = fichas?.[otra] ?? null;

    const pestanas: Pestana[] = sugerencia?.status === 'merged' ? ['fusion'] : manual ? (pareja === null ? ['elegir'] : ['elegir', 'comparar', 'historial']) : ['comparar', 'historial'];

    const nombreA = sugerencia?.customers?.[0]?.display_name ?? fichas?.[0].display_name ?? elegidos[0]?.display_name ?? '';
    const nombreB = sugerencia?.customers?.[1]?.display_name ?? fichas?.[1].display_name ?? elegidos[1]?.display_name ?? '';
    const titulo = manual && pareja === null ? 'Fusionar dos clientes' : nombreA === nombreB ? `${nombreA} (×2)` : `${nombreA} y ${nombreB}`;
    const motivo = sugerencia === null ? null : MOTIVO[sugerencia.reason];
    const detectada = sugerencia === null ? '' : cuando(sugerencia.created_at);
    const subtitulo = manual && pareja === null
        ? 'Elige las dos fichas de tu negocio que son la misma persona.'
        : motivo === null
          ? 'Elegidas a mano'
          : `${motivo[1]} · detectada ${detectada === 'Hoy' || detectada === 'Ayer' ? detectada.toLowerCase() : `el ${detectada}`}`;

    // Permisos: cada canal y finalidad con algún estado, y lo que queda (gana la baja, G26).
    const filasPermisos: { etiqueta: string; a: string; b: string; queda: string }[] = [];
    const bajas: string[] = [];
    if (fichas !== null) {
        for (const [canal, nombreCanal] of CANALES) {
            for (const [finalidad, nombreFin] of FINALIDADES) {
                const a = estadoPermiso(fichas[0], canal, finalidad);
                const b = estadoPermiso(fichas[1], canal, finalidad);
                if (a === 'none' && b === 'none') {
                    continue;
                }
                const queda = a === 'withdrawn' || b === 'withdrawn' ? 'withdrawn' : a === 'granted' || b === 'granted' ? 'granted' : a === 'pending_confirmation' || b === 'pending_confirmation' ? 'pending_confirmation' : 'none';
                if (queda === 'withdrawn' && (a === 'granted' || b === 'granted')) {
                    bajas.push(`${nombreCanal} ${nombreFin}`);
                }
                filasPermisos.push({ etiqueta: `${nombreCanal} · ${nombreFin}`, a, b, queda });
            }
        }
    }

    const deLaOtra = fichas === null || quedan === null || datos === null
        ? []
        : DATOS_QUE_SE_QUEDAN.filter(([dato]) => quedan[dato] === otra && valorDe(fichas[0], dato, datos.campos) !== valorDe(fichas[1], dato, datos.campos));

    const fusionarYa = (confirmado: boolean) => {
        if (P === null || O === null) {
            return;
        }
        setOcupado(true);
        ops.mergeCustomers(
            negocio,
            {
                principal_customer_id: P.id,
                absorbed_customer_id: O.id,
                ...(sugerencia === null ? {} : { suggestion_id: sugerencia.id }),
                keep_from_absorbed: deLaOtra.map(([dato]) => dato),
                confirm_different_person_risk: confirmado,
            },
            claveIdempotencia(),
        )
            .then(() => {
                setVentana(false);
                onFusionada(`${P.display_name} y ${O.display_name} son ya una sola ficha. Su historial, sus reservas y sus pagos están en ${primerNombre(P.display_name)}.`);
            })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'confirmation_required') {
                    setPideConfirmar(e.problema.detail ?? TEXTOS.confirmoAviso);
                    setConfirmo(false);
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido fusionar las fichas. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    const tarjeta = (lado: Lado) => {
        if (fichas === null || datos === null) {
            return null;
        }
        const { campos } = datos;
        const f = fichas[lado];
        const elegida = principal === lado;
        const visitas = f.stats?.visits_count ?? 0;
        const actividad = [`${String(visitas)} ${visitas === 1 ? 'visita' : 'visitas'}`, importe(f.stats?.total_spent), f.stats?.last_visit_at == null ? '' : `última ${diaMes(f.stats.last_visit_at)}`].filter((t) => t !== '').join(' · ');
        return (
            <button
                key={f.id}
                type="button"
                role="radio"
                aria-checked={elegida}
                aria-disabled={bloqueada ? true : undefined}
                className={`rv-dup-perfil${elegida && !bloqueada ? ' rv-dup-perfil--on' : ''}`}
                onClick={() => {
                    if (!bloqueada) {
                        setPrincipal(lado);
                        setQuedan(quedanPorDefecto(fichas, lado, campos));
                    }
                }}
            >
                <span className="rv-dup-perfil__cab">
                    <span className={`rv-ca-inicial${f.status === 'linked' ? ' rv-ca-inicial--roja' : ''}`}>{inicialesDe(f.display_name)}</span>
                    <span className="rv-dup-perfil__nombre">
                        <strong>{f.display_name}</strong>
                        <span>
                            <Chip estado={f.status === 'linked' ? 'confirmed' : 'neutral'}>{f.status === 'linked' ? 'Vinculado' : 'Local'}</Chip>
                        </span>
                    </span>
                    {!bloqueada && (
                        <span className="rv-opcion__radio" aria-hidden="true">
                            <span />
                        </span>
                    )}
                </span>
                <span className="rv-dup-perfil__lineas">
                    {[
                        ['Teléfono', telefonoDe(f) || '—'],
                        ['Email', emailDe(f) || '—'],
                        ['Alta', [diaMes(f.created_at), f.source === undefined ? '' : (ORIGEN[f.source] ?? '')].filter((t) => t !== '').join(' · ')],
                        ['Actividad', actividad],
                    ].map(([k, v]) => (
                        <span key={k}>
                            <span>{k}</span>
                            <strong>{v}</strong>
                        </span>
                    ))}
                </span>
                {!bloqueada && <span className={`rv-dup-perfil__papel${elegida ? ' rv-dup-perfil__papel--on' : ''}`}>{elegida ? 'Principal: es la ficha que se queda' : 'Se une a la principal'}</span>}
            </button>
        );
    };

    const vistaComparar = () => {
        if (fichas === null || datos === null || quedan === null) {
            return error !== null ? <EstadoError seccion="las fichas" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} /> : <EstadoCargando seccion="las fichas" />;
        }
        const vinculadas = fichas.filter((f) => f.status === 'linked');
        const nota = estadoSug === 'dismissed' ? TEXTOS.descartada : !puedeFusionar ? (soloLectura ? TEXTOS.compararNegocio : TEXTOS.compararRol) : null;
        const etiquetas = [...new Set([...(fichas[0].tag_ids ?? []), ...(fichas[1].tag_ids ?? [])])].map((t) => datos.etiquetas[t] ?? '').filter((t) => t !== '');
        const visitas = (fichas[0].stats?.visits_count ?? 0) + (fichas[1].stats?.visits_count ?? 0);
        const conNombres = fichas[0].display_name === fichas[1].display_name;
        const quien = (lado: Lado) => `${fichas[lado].display_name}${conNombres ? ` (${String(lado + 1)})` : ''}`;
        return (
            <div className="rv-ca-cuerpo">
                {nota !== null && (
                    <div className="rv-ca-caja">
                        <span className="rv-ca-caja__fila">
                            <Icono nombre={estadoSug === 'dismissed' ? 'users' : 'eye'} />
                            <span>{nota}</span>
                        </span>
                    </div>
                )}
                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <strong>{bloqueada ? 'Las dos fichas' : 'Elige la principal'}</strong>
                        <span>{bloqueada ? 'Lo que el negocio sabe de cada una.' : 'Es la que se queda. La otra se une a ella.'}</span>
                    </div>
                    <div role="radiogroup" aria-label="Perfil principal" className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                        {tarjeta(0)}
                        {tarjeta(1)}
                    </div>
                    {vinculadas.length > 0 && (
                        <p className="rv-ca-nota">
                            <Icono nombre="lock" />
                            <span>{`${vinculadas.map((f) => f.display_name).join(' y ')} ${vinculadas.length > 1 ? 'tienen' : 'tiene'} Cuenta Reservaria. Su nombre, teléfono y email verificados no cambian al fusionar: solo los cambia el cliente desde «Mis datos».`}</span>
                        </p>
                    )}
                </div>

                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <strong>Datos que se quedan</strong>
                        <span>{bloqueada ? 'Datos que el negocio guarda de cada ficha.' : 'Elige en cada línea con qué valor te quedas. Por defecto, el de la principal.'}</span>
                    </div>
                    <div className="rv-dup-quedan">
                        {DATOS_QUE_SE_QUEDAN.map(([dato, etiqueta]) => {
                            const va = valorDe(fichas[0], dato, datos.campos);
                            const vb = valorDe(fichas[1], dato, datos.campos);
                            return (
                                <div key={dato} className={`rv-dup-queda${movil ? ' rv-dup-queda--movil' : ''}`}>
                                    <span className="rv-dup-queda__etiqueta">{etiqueta}</span>
                                    {va === vb ? (
                                        <span className="rv-dup-queda__igual">
                                            <strong>{va === '' ? '—' : va}</strong> · igual en los dos
                                        </span>
                                    ) : (
                                        ([0, 1] as const).map((lado) => {
                                            const v = lado === 0 ? va : vb;
                                            const on = quedan[dato] === lado;
                                            return (
                                                <button
                                                    key={lado}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={on}
                                                    aria-label={`${etiqueta}: ${v === '' ? 'vacío' : v}`}
                                                    aria-disabled={bloqueada ? true : undefined}
                                                    className={`rv-dup-valor${on ? (bloqueada ? ' rv-dup-valor--visto' : ' rv-dup-valor--on') : ''}`}
                                                    onClick={() => {
                                                        if (!bloqueada) {
                                                            setQuedan({ ...quedan, [dato]: lado });
                                                        }
                                                    }}
                                                >
                                                    <span className="rv-dup-valor__radio" aria-hidden="true">
                                                        <span />
                                                    </span>
                                                    <span className={`rv-dup-valor__v${v === '' ? ' rv-dup-valor__v--vacio' : ''}`}>{v === '' ? 'Vacío' : v}</span>
                                                    <span className="rv-dup-valor__quien">{lado === principal ? 'Principal' : 'La otra'}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <strong>Se suman</strong>
                        <span>{TEXTOS.seSumanAyuda}</span>
                    </div>
                    <div>
                        {[
                            ['tags', 'Etiquetas', etiquetas.length === 0 ? 'Ninguna' : etiquetas.join(', ')],
                            ['sticky-note', 'Notas', datos.conNotas.some(Boolean) ? 'Las de las dos fichas' : 'Ninguna'],
                            ['door-open', 'Evaluaciones de puerta', datos.conPuerta.some(Boolean) ? 'Las de las dos fichas' : 'Ninguna'],
                            ['history', 'Historial y contadores', `${String(visitas)} ${visitas === 1 ? 'visita' : 'visitas'} · reservas, pagos y conversaciones de las dos`],
                        ].map(([icono, k, v]) => (
                            <div key={k} className="rv-ca-dato">
                                <span className="rv-dup-suma">
                                    <Icono nombre={icono as 'tags'} />
                                    {k}
                                </span>
                                <strong>{v}</strong>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <strong>Permisos</strong>
                        <span>{TEXTOS.permisosAyuda}</span>
                    </div>
                    {bajas.length > 0 && (
                        <div role="note" className="rv-ca-caja rv-ca-caja--aviso">
                            <span className="rv-ca-caja__fila">
                                <Icono nombre="triangle-alert" />
                                <span>
                                    <strong>Gana la baja.</strong> {`Una de las dos fichas se dio de baja de ${bajas.join(' y ')}. La ficha fusionada queda con la baja y no recibirá ese tipo de mensajes.`}
                                </span>
                            </span>
                        </div>
                    )}
                    {filasPermisos.length > 0 && (
                        <div className="rv-dup-permisos" role="table" aria-label="Permisos de las dos fichas">
                            {!movil && (
                                <div role="row" className="rv-dup-permisos__cab">
                                    <span role="columnheader">Canal</span>
                                    <span role="columnheader">{quien(0)}</span>
                                    <span role="columnheader">{quien(1)}</span>
                                    <span role="columnheader">Queda</span>
                                </div>
                            )}
                            {filasPermisos.map((f) => (
                                <div key={f.etiqueta} role="row" className={`rv-dup-permisos__fila${movil ? ' rv-dup-permisos__fila--movil' : ''}`}>
                                    <strong role="cell">{f.etiqueta}</strong>
                                    {[f.a, f.b, f.queda].map((estado, i) => {
                                        const [texto, tono] = ESTADO_PERMISO[estado] ?? ['Sin permiso', 'neutral'];
                                        return (
                                            <span role="cell" key={i}>
                                                <Chip estado={tono}>{texto}</Chip>
                                            </span>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <p className="rv-ca-nota rv-ca-nota--gris">
                    <Icono nombre="lock" />
                    <span>{TEXTOS.noSeDeshace}</span>
                </p>

                {puedeActuar && sugerencia !== null && (
                    <div className="rv-dup-distintas">
                        <Boton tipo="secundario" icono="users" movil={movil} onClick={() => { onDescartar(sugerencia); }}>
                            Son personas distintas
                        </Boton>
                        <span>{TEXTOS.sonDistintasNota}</span>
                    </div>
                )}
            </div>
        );
    };

    const vistaHistorial = () => {
        if (fichas === null || historial === null) {
            return error !== null ? <EstadoError seccion="el historial" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setHistorial(null); setError(null); }} movil={movil} /> : <EstadoCargando seccion="el historial" />;
        }
        return (
            <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                {([0, 1] as const).map((lado) => {
                    const f = fichas[lado];
                    const visitas = f.stats?.visits_count ?? 0;
                    return (
                        <div key={f.id} className="rv-dup-historial">
                            <div className="rv-dup-historial__cab">
                                <span className={`rv-ca-inicial${f.status === 'linked' ? ' rv-ca-inicial--roja' : ''}`}>{inicialesDe(f.display_name)}</span>
                                <span>
                                    <strong>{f.display_name}</strong>
                                    <small>{lado === principal && puedeActuar ? 'Principal' : `${String(visitas)} ${visitas === 1 ? 'visita' : 'visitas'} · ${importe(f.stats?.total_spent)}`}</small>
                                </span>
                            </div>
                            {historial[lado].length === 0 ? (
                                <div className="rv-dup-historial__vacio">{TEXTOS.sinActividad}</div>
                            ) : (
                                historial[lado].map((l) => (
                                    <div key={l.id} className="rv-dup-historial__linea">
                                        <span className="rv-ca-baldosa">
                                            <Icono nombre="history" />
                                        </span>
                                        <span>
                                            <span>{l.summary ?? l.event_type}</span>
                                            <small>{diaMes(l.occurred_at)}</small>
                                        </span>
                                    </div>
                                ))
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const vistaFusion = () => (
        <div>
            {[
                ['Fichas', `${nombreA} y ${nombreB}`],
                ['Qué coincidía', motivo?.[1] ?? 'Elegidas a mano'],
                ['Detectada', detectada],
            ].map(([k, v]) => (
                <div key={k} className="rv-ca-dato">
                    <span>{k}</span>
                    <strong>{v}</strong>
                </div>
            ))}
            <p className="rv-ca-nota rv-ca-nota--gris" style={{ paddingTop: 14 }}>
                <Icono nombre="lock" />
                <span>{TEXTOS.fusionadaNota}</span>
            </p>
        </div>
    );

    const vistaElegir = () => (
        <div className="rv-ca-cuerpo">
            <div className={`rv-ca-dos${movil ? ' rv-ca-dos--movil' : ''}`}>
                {([0, 1] as const).map((lado) => {
                    const c = elegidos[lado];
                    const lista = resultados[lado];
                    return (
                        <div key={lado} className="rv-ca-buscar">
                            <strong className="rv-dup-elegir__titulo">{lado === 0 ? 'Primera ficha' : 'Segunda ficha'}</strong>
                            {c !== null ? (
                                <div className="rv-ca-elegido">
                                    <span className="rv-ca-inicial">{inicialesDe(c.display_name)}</span>
                                    <span>
                                        <strong>{c.display_name}</strong>
                                        <small>{[c.phone ?? '', `${String(c.visits_count ?? 0)} ${(c.visits_count ?? 0) === 1 ? 'visita' : 'visitas'}`].filter((t) => t !== '').join(' · ')}</small>
                                    </span>
                                    <Boton tipo="secundario" icono="x" onClick={() => { setElegidos(lado === 0 ? [null, elegidos[1]] : [elegidos[0], null]); setPareja(null); }}>
                                        Cambiar
                                    </Boton>
                                </div>
                            ) : (
                                <>
                                    <Campo tipo="search" etiqueta="Nombre, teléfono o email" valor={busquedas[lado]} nombre={`buscar-cliente-${String(lado)}`} onChange={(v) => { setBusquedas(lado === 0 ? [v, busquedas[1]] : [busquedas[0], v]); }} />
                                    {lista !== null && (
                                        <div className="rv-ca-resultados">
                                            {lista.map((r) => (
                                                <button key={r.id} type="button" className="rv-ca-resultado" onClick={() => { setElegidos(lado === 0 ? [r, elegidos[1]] : [elegidos[0], r]); }}>
                                                    <span className="rv-ca-inicial">{inicialesDe(r.display_name)}</span>
                                                    <span>
                                                        <strong>{r.display_name}</strong>
                                                        <small>{[r.phone ?? '', r.email ?? ''].filter((t) => t !== '').join(' · ')}</small>
                                                    </span>
                                                    <Icono nombre="plus" />
                                                </button>
                                            ))}
                                            {lista.length === 0 && <div className="rv-dup-sin">{`No hay clientes con «${busquedas[lado].trim()}».`}</div>}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    );
                })}
            </div>
            <Boton
                icono="git-compare"
                movil={movil}
                disabled={elegidos[0] === null || elegidos[1] === null}
                className="rv-dup-comparar"
                onClick={() => {
                    const [a, b] = elegidos;
                    if (a !== null && b !== null) {
                        setPareja([a.id, b.id]);
                        setPestana('comparar');
                    }
                }}
            >
                Comparar
            </Boton>
        </div>
    );

    const ayuda = AYUDA[pestana === 'fusion' ? 'fusionada' : pestana];
    const lineasVentana: [string, string, boolean][] = P === null || O === null
        ? []
        : [
              ['Se queda', P.display_name, false],
              ['Se une', O.display_name, false],
              ['De la otra ficha', deLaOtra.length === 0 ? 'Nada: todo de la principal' : deLaOtra.map(([, e]) => e.toLocaleLowerCase('es')).join(', '), false],
              ['Se suman', 'Etiquetas, notas, historial y contadores', false],
              ['Permisos', bajas.length > 0 ? `Gana la baja: ${bajas.join(', ')}` : 'Se unen sin cambios', bajas.length > 0],
          ];

    return (
        <>
            <PanelLateral
                abierto={abierto}
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={titulo}
                eyebrow={manual ? 'Fusionar a mano' : estadoSug === 'merged' ? 'Fusionada' : 'Posible duplicado'}
                subtitulo={subtitulo}
                icono={estadoSug === 'merged' ? 'git-merge' : 'git-compare'}
                accion={puedeActuar ? 'Fusionar' : ''}
                accionIcono="git-merge"
                onAccion={() => {
                    setPideConfirmar(null);
                    setConfirmo(false);
                    setVentana(true);
                }}
            >
                <div className="rv-ca-cuerpo">
                    <div className="rv-dup-panel-cab">
                        {pestanas.length > 1 ? (
                            <div role="tablist" aria-label="Apartados de la comparación" className="rv-ca-botonera">
                                {pestanas.map((p) => (
                                    <button key={p} type="button" role="tab" aria-selected={pestana === p} className="rv-ca-botonera__b" onClick={() => { setPestana(p); }}>
                                        <Icono nombre={PESTANAS[p][1]} />
                                        {PESTANAS[p][0]}
                                    </button>
                                ))}
                            </div>
                        ) : (
                            <span />
                        )}
                        <Ayuda tarjetas={ayuda} />
                    </div>
                    {abierto && pestana === 'elegir' && vistaElegir()}
                    {abierto && pestana === 'comparar' && vistaComparar()}
                    {abierto && pestana === 'historial' && vistaHistorial()}
                    {abierto && pestana === 'fusion' && vistaFusion()}
                </div>
            </PanelLateral>
            <Ventana
                abierta={ventana && P !== null}
                titulo={P === null ? '' : `Fusionar en ${P.display_name}`}
                texto={P === null || O === null ? '' : `${O.display_name} se une a ${P.display_name}. Sus reservas, citas, pagos y conversaciones pasan a la principal. No se puede deshacer desde el panel.`}
                accion="Fusionar"
                accionIcono="git-merge"
                ocupado={ocupado}
                ocupadoTexto="Fusionando…"
                inactiva={pideConfirmar !== null && !confirmo}
                movil={movil}
                onAccion={() => {
                    fusionarYa(pideConfirmar !== null && confirmo);
                }}
                onCerrar={() => {
                    setVentana(false);
                }}
            >
                <div className="rv-ca-ventana-lineas">
                    {lineasVentana.map(([k, v, rojo]) => (
                        <div key={k} className="rv-ca-ventana-linea">
                            <span>{k}</span>
                            <strong className={rojo ? 'rv-ca-rojo' : undefined}>{v}</strong>
                        </div>
                    ))}
                </div>
                {pideConfirmar !== null && (
                    <div className="rv-ca-caja rv-ca-caja--aviso" role="alert">
                        <span className="rv-ca-caja__fila">
                            <Icono nombre="triangle-alert" />
                            <span>{pideConfirmar}</span>
                        </span>
                        <Casilla marcada={confirmo} onCambio={setConfirmo}>
                            {TEXTOS.confirmoMisma}
                        </Casilla>
                    </div>
                )}
            </Ventana>
        </>
    );
}
